import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { cx } from '@linaria/core';
import { styled } from '@linaria/react';
import { GuestReviews } from 'components/Accommodation/ReviewsSection/GuestReviews';
import { toggle } from 'opticks';
import { equals } from 'ramda';
import { getLanguageCode } from 'utils/localization/language';
import { useDeviceLayout } from '@daedalus/atlas/context/deviceLayout';
import { Divider } from '@daedalus/atlas/Divider';
import { Box } from '@daedalus/atlas/helpers/Box';
import { Text } from '@daedalus/atlas/Text';
import { trackEvent } from '@daedalus/core/src/analytics/modules/actions';
import { Action, Category, Entity } from '@daedalus/core/src/analytics/types/Events';
import FormattedMessageWrapper from '@daedalus/core/src/localization/components/FormattedMessage';
import { useGetUserReviewsQuery } from '@daedalus/core/src/sapi/services/searchApi';
import { Review } from '@findhotel/sapi';
import { ReviewsLoadingList } from '../ReviewLoading';
import { reviewMessages } from '../ReviewMessages';
import { ReviewSearchInput } from '../ReviewSearchInput';
import { ReviewSorting } from '../ReviewSorting';
const ReviewCommentsWrapper = /*#__PURE__*/styled('div')({
  name: "ReviewCommentsWrapper",
  class: "r8pw3oo",
  propsAsIs: false
});
interface Props {
  hotelId?: string;
  isOverlay?: boolean;
  hasGuestReviews: boolean;
  isHotelDetailsTab?: boolean;
}
export const ReviewsList: React.FC<Props> = ({
  hotelId,
  isOverlay = false,
  hasGuestReviews,
  isHotelDetailsTab = false
}) => {
  const {
    isMobile
  } = useDeviceLayout();
  const {
    formatMessage
  } = useIntl();
  const languageCode = useSelector(getLanguageCode);
  const dispatch = useDispatch();
  const isGuestReviewsLanguageBSide = toggle('f72fa251-guest-reviews-languages', false, true);
  const isSortFilterOnBSide = toggle('9633c72c-guest-reviews-sort-filter', false, true);
  const [offset, setOffset] = useState(0);
  const [storedReviews, setStoredReviews] = useState<Review[]>([]);

  // Added as a part of 9633c72c-guest-reviews-sort-filter
  const [sortBy, setSortBy] = useState(null);
  const [searchReviews, setSearchReviews] = useState<string[]>([]);
  const {
    data,
    isLoading: isReviewsRequestLoading,
    isFetching: isReviewRequestFetching
  } = useGetUserReviewsQuery({
    hotelId,
    language: languageCode,
    pageSize: 10,
    offset,
    sortBy,
    ...(searchReviews.length > 0 && {
      search: searchReviews
    })
  }, {
    skip: !hasGuestReviews || !hotelId
  });
  const handleSearchReviews = (searches: string[]) => {
    if (searches.length > 0) {
      setSearchReviews(prev => {
        if (!equals(prev, searches)) {
          setOffset(0);
          setStoredReviews([]);
          return searches;
        }
        return prev;
      });
    } else {
      setSearchReviews(prev => {
        if (prev.length > 0) {
          setOffset(0);
          setStoredReviews([]);
        }
        return [];
      });
    }
  };
  const handleReviewSortChange = useCallback((value: string) => {
    if (value !== sortBy) {
      // reset list
      setStoredReviews([]);
      // reset offset
      setOffset(0);
      // update sort
      setSortBy(value);
    }
  }, [sortBy]);
  useEffect(() => {
    if (data?.reviews?.length > 0) {
      setStoredReviews(prev => [...prev, ...data.reviews]);
    }
  }, [data, isGuestReviewsLanguageBSide]);
  useEffect(() => {
    if (data?.hotelId && searchReviews.length > 0) {
      dispatch(trackEvent({
        category: Category.System,
        entity: Entity.ReviewsFetch,
        action: Action.Succeeded,
        payload: {
          hotelId,
          reviewsCount: data?.reviewsAvailable,
          SearchKeyword: searchReviews.join(',')
        }
      }));
    }
  }, [searchReviews, data, hotelId, dispatch]);
  const reviewsData = useMemo(() => {
    if (isGuestReviewsLanguageBSide) return storedReviews;
    return storedReviews.filter(review => review.language === 'en');
  }, [storedReviews, isGuestReviewsLanguageBSide]);
  const showLoadingState = useMemo(() => reviewsData.length == 0 && (isReviewRequestFetching || isReviewsRequestLoading), [reviewsData, isReviewRequestFetching, isReviewsRequestLoading]);
  const showNoResults = useMemo(() => reviewsData.length == 0 && searchReviews.length > 0, [reviewsData, searchReviews]);
  const noMoreReviews = useMemo(() => {
    return Boolean(data?.nextOffset < 1 || !isGuestReviewsLanguageBSide && data?.reviews?.find(review => review.language !== 'en'));
  }, [data, isGuestReviewsLanguageBSide]);
  if (isOverlay) {
    return <ReviewCommentsWrapper className={cx(!isHotelDetailsTab && '__accommodationReviewsSection')}>
        {isMobile && <Box paddingBottom="s700">
            <Divider />
          </Box>}
        <Box paddingBottom="s500" display="flex" alignItems="center" justifyContent="space-between">
          <Text variant="titleL">
            <FormattedMessageWrapper id="guestReviews" defaultMessage="What guests say" />
          </Text>
          {isSortFilterOnBSide && <ReviewSorting onChange={handleReviewSortChange} />}
        </Box>
        {isSortFilterOnBSide && <ReviewSearchInput onSearch={handleSearchReviews} />}
        {showLoadingState ? <ReviewsLoadingList /> : showNoResults ? <Box display="flex" flexDirection="column" alignItems="center" gap="s300" paddingTop="s700" paddingBottom="s950">
            <Text variant="titleM" align="center">
              {formatMessage(reviewMessages.reviewSectionNoResultsTitle)}
            </Text>
            <Text variant="bodyS" align="center">
              {formatMessage(reviewMessages.reviewSectionNoResultsDescription)}
            </Text>
          </Box> : <GuestReviews key="reviewComments" mountedOnOverlay reviewsData={reviewsData} noMoreReviews={noMoreReviews} onLoadMore={() => setOffset(data?.nextOffset)} sourceComponent={isHotelDetailsTab ? 'HotelDetailsTab' : 'AccommodationReviewsSection'} />}
      </ReviewCommentsWrapper>;
  }
  if (reviewsData?.length > 0) {
    return <ReviewCommentsWrapper className="__accommodationReviewsSection">
        {isMobile && <Box paddingBottom="s700">
            <Divider />
          </Box>}
        <Box marginBottom="s600" display="flex" alignItems="center" justifyContent="space-between">
          <Text variant="titleS">
            <FormattedMessageWrapper id="guestReviews" defaultMessage="What guests say" />
          </Text>
        </Box>
        <GuestReviews key="reviewComments" reviewsData={reviewsData} noMoreReviews={noMoreReviews} onLoadMore={() => setOffset(data?.nextOffset)} sourceComponent="AccommodationReviewsSection" />
      </ReviewCommentsWrapper>;
  }
  return null;
};

require("./ReviewsList.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./ReviewsList.tsx");